<template>
  <router-link v-if="!!to" :to="to" :href="href" v-bind="$attrs" :class="buttonClasses">
    <slot></slot>
  </router-link>

  <a v-else-if="!!href" :href="href" v-bind="$attrs" :class="buttonClasses">
    <slot></slot>
  </a>

  <button v-else v-bind="$attrs" :class="buttonClasses">
    <slot></slot>
  </button>
</template>

<script lang="ts">
export const buttonVariants = ["primary", "secondary", "secondary-grey", "tertiary", "ghost", "ghost-neutral"] as const;
export const buttonSizes = ["xs", "sm", "md", "lg"] as const;
export type ButtonVariant = (typeof buttonVariants)[number];
export type ButtonSize = (typeof buttonSizes)[number];
</script>

<script setup lang="ts">
import { computed } from "vue";
import type { RouterLinkProps } from "vue-router";

defineOptions({ name: "Button" });

const props = withDefaults(
  defineProps<{
    href?: string | null;
    to?: RouterLinkProps["to"] | null;
    variant?: ButtonVariant;
    accent?: boolean;
    icon?: boolean;
    size?: ButtonSize;
    packed?: boolean;
    disabled?: boolean;
  }>(),
  {
    href: null,
    to: null,
    variant: "primary",
    accent: false,
    icon: false,
    size: "md",
    packed: false,
    disabled: false,
  },
);

const buttonClasses = computed(() => [
  "button",
  `button-${props.variant}`,
  `button-${props.size}`,
  { "button-icon": props.icon },
  { "button-packed": props.packed },
  { "button-disabled": props.disabled },
  { "button-accent": props.accent },
]);
</script>

<style lang="scss">
.button {
  @apply inline-flex justify-center items-center gap-2;
  @apply font-bold text-center whitespace-nowrap leading-[-10px];
  @apply transition-colors duration-200;
  @apply cursor-pointer;
}

.button-primary {
  @apply border border-transparent rounded-theme hover:border-current;

  @apply bg-theme-primary text-theme-primary-contrast hover:bg-theme-primary-contrast hover:text-theme-primary;

  &.button-accent {
    @apply bg-theme-accent text-theme-accent-contrast hover:bg-theme-accent-contrast hover:text-theme-accent;
  }
}

.button-secondary {
  @apply border rounded-theme;
  @apply bg-theme-primary-contrast text-theme-primary border-theme-primary hover:bg-theme-primary hover:text-theme-primary-contrast;

  &.button-accent {
    @apply bg-theme-accent-contrast text-theme-accent border-theme-accent hover:bg-theme-accent hover:text-theme-accent-contrast;
  }
}

.button-secondary-grey {
  @apply border rounded-theme bg-white text-button-grey border-button-grey hover:text-black hover:border-black;
}

.button-tertiary {
  @apply bg-transparent border border-transparent rounded-theme hover:border-current;
  @apply text-theme-primary hover:bg-theme-primary-contrast hover:text-theme-primary;

  &.button-accent {
    @apply text-theme-accent hover:bg-theme-accent-contrast hover:text-theme-accent;
  }
}

.button-ghost {
  @apply bg-transparent text-theme-primary hover:text-theme-accent;

  &.button-accent {
    @apply text-theme-accent hover:bg-theme-accent-contrast hover:text-theme-accent;
  }
}

.button-ghost-neutral {
  @apply bg-transparent text-black;

  &:hover {
    @apply underline;
  }

  &.button-icon{
    @apply rounded-full duration-200;

    svg{
      @apply duration-200;
    }

    &:hover{
      @apply bg-black;

      svg {
        @apply text-white;
      }
    }
  }
}

.button-icon {
  @apply p-0 rounded-none;
}

.button-xs {
  @apply h-8 px-2;

  &.button-icon {
    @apply w-8 p-0;
  }
}

.button-sm {
  @apply h-10 px-4;

  &.button-icon {
    @apply w-10 p-0;
  }
}

.button-md {
  @apply h-12 px-6 text-lg;

  &.button-icon {
    @apply w-12 p-0;
  }
}

.button-lg {
  @apply h-14 px-6 text-lg;

  &.button-icon {
    @apply w-14 p-0;
  }
}

.button-packed {
  @apply px-0;
}

.button:disabled {
  @apply cursor-not-allowed;
  @apply bg-theme-muted text-theme-muted;
  @apply border-theme-muted;
}

.button-group {
  @apply flex;

  & .button-group-divider {
    @apply border-y-8 w-[1px] border-y-theme-primary;
  }
}
.button-group > .button,
.button-group > *:has(> .button) {
  @apply rounded-none;
}
.button-group > .button:first-child,
.button-group > :first-child > .button {
  @apply rounded-none rounded-l-theme;
}
.button-group > .button:last-child,
.button-group > :last-child > .button {
  @apply rounded-none rounded-r-theme;
}

.button-disabled {
  @apply pointer-events-none opacity-50;
}
</style>
